import { memo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import type { ArticleSummary } from '@/types/newsService';
import { useHaptics } from '@/hooks/useHaptics';

interface Props {
  articles: ArticleSummary[];
  locale?: string;
}

/**
 * Horizontal row of compact "trending" cards beneath the featured carousel.
 * Thumbnail + title + meta (reading time, upvotes, primary tag name).
 */
export const TrendingSection = memo(function TrendingSection({
  articles,
  locale = 'en',
}: Props) {
  const navigate = useNavigate();
  const { t } = useTranslation('news');
  const haptics = useHaptics();

  const handleNavigate = useCallback(
    (slug: string) => {
      haptics.selection();
      navigate(`/news/${slug}`);
    },
    [navigate, haptics],
  );

  if (articles.length === 0) return null;

  return (
    <div className="mb-2">
      <h2 className="mb-3 px-4 text-base font-bold text-fg">
        {t('section.trending', { defaultValue: 'Trending Now' })}
      </h2>
      <div className="no-scrollbar overflow-x-auto">
        <div className="flex gap-3 px-4">
          {articles.map((item) => {
            const localized = item.content.find((c) => c.locale === locale) ?? item.content[0];
            if (!localized) return null;
            return (
              <button
                key={item._id}
                type="button"
                onClick={() => handleNavigate(item.slug)}
                className="flex w-[260px] shrink-0 items-center gap-2.5 rounded-xl bg-bg-secondary p-2.5 text-left transition-opacity active:opacity-80"
              >
                {item.heroImage ? (
                  <img
                    src={item.heroImage.thumbnailUrl || item.heroImage.url}
                    alt={item.heroImage.alt}
                    loading="lazy"
                    className="h-14 w-14 shrink-0 rounded-lg object-cover"
                  />
                ) : (
                  <div className="h-14 w-14 shrink-0 rounded-lg bg-border" />
                )}
                <div className="min-w-0 flex-1">
                  <h3 className="mb-1 line-clamp-2 text-[13px] font-semibold leading-[18px] text-fg">
                    {localized.title}
                  </h3>
                  <p className="text-[11px] text-[#6a6a6a]">
                    {item.readingTimeMinutes} min · ▲ {item.engagement.upvotes}
                    {item.primaryTag?.name ? (
                      <span
                        className="font-bold"
                        style={{ color: item.primaryTag.color ?? '#00e7df' }}
                      >
                        {' · '}
                        {item.primaryTag.name}
                      </span>
                    ) : null}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
});
